import { useEffect, useRef, useId } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import mermaid from 'mermaid'
import './markdown-preview.css'

mermaid.initialize({
    startOnLoad: false,
    theme: 'neutral',
    securityLevel: 'loose',
    fontFamily: 'inherit',
})

/**
 * Renders a ```mermaid code block into an SVG diagram.
 */
function MermaidBlock({ code }: { code: string }) {
    const ref = useRef<HTMLDivElement>(null)
    const id = useId().replace(/:/g, '_')

    useEffect(() => {
        let cancelled = false
            ; (async () => {
                try {
                    const { svg } = await mermaid.render(`mermaid-${id}`, code)
                    if (!cancelled && ref.current) {
                        ref.current.innerHTML = svg
                    }
                } catch {
                    if (!cancelled && ref.current) {
                        ref.current.textContent = code
                    }
                }
            })()
        return () => { cancelled = true }
    }, [code, id])

    return <div ref={ref} className="md-mermaid" />
}

interface MarkdownPreviewProps {
    content: string
    className?: string
}

export function MarkdownPreview({ content, className = '' }: MarkdownPreviewProps) {
    return (
        <div className={`md-preview ${className}`}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                    code({ className: codeClass, children, ...props }) {
                        const match = /language-mermaid/.exec(codeClass || '')
                        if (match) {
                            return <MermaidBlock code={String(children).trim()} />
                        }
                        // Inline code
                        if (!codeClass) {
                            return <code {...props}>{children}</code>
                        }
                        // Fenced code block
                        return (
                            <code className={codeClass} {...props}>
                                {children}
                            </code>
                        )
                    },
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    )
}

export default MarkdownPreview
